const DEFAULT_CLIMATE_STATION = Object.freeze({
  stationUid: "",
  sourceType: "wunderground",
  stationId: "",
  stationName: "",
  apiKey: "",
  unitSystem: "m",
  useTemperature: true,
  useHumidity: true,
  usePressure: true,
  useRainfall: true,
  useWind: false,
  useUv: false,
  useSolarRadiation: false,
  useDewPoint: false,
  latitude: "",
  longitude: "",
  priority: 1,
  integrationStatus: "pending",
  lastConnectionMessage: "",
  lastObservedOn: "",
  lastAutoSyncAt: "",
  lastAutoSyncStatus: "pending",
  lastAutoSyncMessage: "",
  lastSuccessSyncAt: "",
  notes: "",
  utcOffsetHours: -3,
});

const DEFAULT_CLIMATE_PROFILE = Object.freeze({
  preferredSource: "wunderground",
  integrationStatus: "pending",
  weatherUndergroundStationId: "",
  weatherUndergroundStationName: "",
  wundergroundBackendMode: "pending",
  fallbackToInmet: true,
  inmetStationCode: "",
  inmetStationName: "",
  maxStationDistanceKm: 80,
  analysisWindowDays: 30,
  alertWindowDays: 30,
  historyWindowDays: 90,
  climateRefreshHours: 1,
  refreshHoursCustomized: false,
  rainfallWeight: 0.7,
  dryDaysWeight: 0.3,
  climateRiskThreshold: 0.9,
  alertsHeatEnabled: true,
  alertsHeatWarningTempC: 34,
  alertsHeatCriticalTempC: 38,
  alertsLowHumidityEnabled: true,
  alertsLowHumidityWarningPct: 35,
  alertsLowHumidityCriticalPct: 25,
  alertsHeavyRainEnabled: true,
  alertsHeavyRainWarningMm: 20,
  alertsHeavyRainCriticalMm: 40,
  alertsDryStreakEnabled: true,
  alertsDryStreakWarningDays: 5,
  alertsDryStreakCriticalDays: 10,
  notes: "",
  wundergroundApiKeyGlobal: "",
  climateRetentionYears: 3,
  lastClimateSyncAt: "",
  lastClimateSource: "",
  lastClimateSummary: "Integração climática pendente",
  rainfallMm15d: "",
  rainfallMm30d: "",
  rainfallMm90d: "",
  dryDays15d: "",
  dryDays30d: "",
  dryDays90d: "",
  avgTemp15d: "",
  avgTemp30d: "",
  avgTemp90d: "",
});


function normalizeUtcDateTimeString(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(raw)) return `${raw.replace(" ", "T")}Z`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) return `${raw}Z`;
  return raw;
}

function clamp(value, min, max, fallback){
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}
function toMetricString(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const partialEditable = /^-?(?:\d+)?(?:[.,](?:\d*)?)?$/;
  if (partialEditable.test(raw) && /[.,-]$/.test(raw)) return raw;
  if (raw in {"-":1,".":1,",":1,"-.":1,"-,":1}) return raw;
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? String(n) : raw;
}
function toMetricNumber(value, fallback = 0) {
  const raw = String(value ?? "").trim().replace(",", ".");
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}


function normalizeClimateStation(input = {}, fallbackSource = "wunderground", fallbackPriority = 1) {
  const base = input && typeof input === "object" ? input : {};
  const sourceRaw = String(base.sourceType || base.source_type || "");
  const sourceType = ["wunderground", "inmet", "manual"].includes(sourceRaw) ? sourceRaw : fallbackSource;
  const unitSystem = ["m", "e", "h"].includes(String(base.unitSystem || base.unit_system || "")) ? String(base.unitSystem || base.unit_system) : "m";
  const boolField = (value, fallback) => typeof value === "boolean" ? value : (value == null ? fallback : !["0","false","off","não","nao"].includes(String(value).trim().toLowerCase()));
  return {
    ...DEFAULT_CLIMATE_STATION,
    ...base,
    stationUid: String(base.stationUid || base.station_uid || base.id || base.stationId || base.station_id || `${sourceType}_${fallbackPriority}`).trim(),
    sourceType,
    stationId: String(base.stationId || base.station_id || base.source_station_id || "").trim(),
    stationName: String(base.stationName || base.station_name || base.source_station_name || ""),
    apiKey: String(base.apiKey || base.api_key || ""),
    unitSystem,
    useTemperature: boolField(base.useTemperature ?? base.use_temperature, true),
    useHumidity: boolField(base.useHumidity ?? base.use_humidity, true),
    usePressure: boolField(base.usePressure ?? base.use_pressure, true),
    useRainfall: boolField(base.useRainfall ?? base.use_rainfall, true),
    useWind: boolField(base.useWind ?? base.use_wind, false),
    useUv: boolField(base.useUv ?? base.use_uv, false),
    useSolarRadiation: boolField(base.useSolarRadiation ?? base.use_solar_radiation, false),
    useDewPoint: boolField(base.useDewPoint ?? base.use_dew_point, false),
    latitude: toMetricString(base.latitude),
    longitude: toMetricString(base.longitude),
    priority: Math.max(1, Math.trunc(toMetricNumber(base.priority, fallbackPriority) || fallbackPriority)),
    integrationStatus: ["pending", "online", "delayed", "offline"].includes(String(base.integrationStatus || base.integration_status || base.connection_status || "")) ? String(base.integrationStatus || base.integration_status || base.connection_status) : "pending",
    lastConnectionMessage: String(base.lastConnectionMessage || base.last_connection_message || ""),
    lastObservedOn: String(base.lastObservedOn || base.last_observed_on || ""),
    lastAutoSyncAt: String(base.lastAutoSyncAt || base.last_auto_sync_at || ""),
    lastAutoSyncStatus: ["pending", "online", "delayed", "offline"].includes(String(base.lastAutoSyncStatus || base.last_auto_sync_status || "")) ? String(base.lastAutoSyncStatus || base.last_auto_sync_status) : "pending",
    lastAutoSyncMessage: String(base.lastAutoSyncMessage || base.last_auto_sync_message || ""),
    lastSuccessSyncAt: String(base.lastSuccessSyncAt || base.last_success_sync_at || ""),
    notes: String(base.notes || ""),
    utcOffsetHours: clamp(base.utcOffsetHours ?? base.utc_offset_hours, -12, 14, -3),
  };
}

function normalizeClimateStations(input = [], profile = null) {
  const list = Array.isArray(input) ? input : [];
  const stations = list.map((item, idx) => normalizeClimateStation(item, item?.sourceType || profile?.preferredSource || "wunderground", idx + 1));
  if (stations.length) return stations.sort((a, b) => (a.priority - b.priority) || String(a.stationName || a.stationId).localeCompare(String(b.stationName || b.stationId)));
  const legacy = [];
  if (profile && (profile.weatherUndergroundStationId || profile.weatherUndergroundStationName)) {
    legacy.push(normalizeClimateStation({
      sourceType: "wunderground",
      stationUid: "legacy_wu",
      stationId: profile.weatherUndergroundStationId,
      stationName: profile.weatherUndergroundStationName,
      integrationStatus: profile.integrationStatus,
    }, "wunderground", 1));
  }
  if (profile && (profile.inmetStationCode || profile.inmetStationName)) {
    legacy.push(normalizeClimateStation({
      sourceType: "inmet",
      stationUid: "legacy_inmet",
      stationId: profile.inmetStationCode,
      stationName: profile.inmetStationName,
      integrationStatus: profile.integrationStatus,
    }, "inmet", legacy.length + 1));
  }
  return legacy;
}

function distanceKm(fromLat, fromLng, toLat, toLng) {
  const a1 = Number(fromLat); const o1 = Number(fromLng); const a2 = Number(toLat); const o2 = Number(toLng);
  if (![a1,o1,a2,o2].every(Number.isFinite)) return null;
  const R = 6371;
  const dLat = (a2 - a1) * Math.PI / 180;
  const dLng = (o2 - o1) * Math.PI / 180;
  const s1 = Math.sin(dLat/2); const s2 = Math.sin(dLng/2);
  const aa = s1*s1 + Math.cos(a1*Math.PI/180) * Math.cos(a2*Math.PI/180) * s2*s2;
  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1-aa));
  return Number((R * c).toFixed(2));
}

function getNearestClimateStation(profile, options = {}) {
  const p = normalizeFarmClimateProfile(profile);
  const stations = normalizeClimateStations(p.stations, p);
  if (!stations.length) return null;
  const preferredSource = ["wunderground", "inmet", "manual"].includes(String(options.preferredSource || "")) ? String(options.preferredSource) : p.preferredSource;
  const latitude = options.latitude;
  const longitude = options.longitude;
  const ranked = stations
    .map((station) => ({
      ...station,
      distanceKm: distanceKm(latitude, longitude, station.latitude, station.longitude),
      sourceRank: station.sourceType === preferredSource ? 0 : (station.sourceType === "inmet" && p.fallbackToInmet ? 1 : 2),
    }))
    .sort((a, b) => {
      if (a.sourceRank !== b.sourceRank) return a.sourceRank - b.sourceRank;
      const ad = a.distanceKm == null ? Number.POSITIVE_INFINITY : a.distanceKm;
      const bd = b.distanceKm == null ? Number.POSITIVE_INFINITY : b.distanceKm;
      if (ad !== bd) return ad - bd;
      return a.priority - b.priority;
    });
  return ranked[0] || null;
}

function normalizeFarmClimateRefreshHours(base) {
  const raw = base && typeof base === "object" ? base.climateRefreshHours : undefined;
  const hasExplicitRefresh = !!(base && typeof base === "object" && Object.prototype.hasOwnProperty.call(base, "climateRefreshHours"));
  const customized = typeof (base && base.refreshHoursCustomized) === "boolean"
    ? !!base.refreshHoursCustomized
    : (hasExplicitRefresh && Number(raw) !== 6);
  let refreshHours = clamp(raw, 0, 168, DEFAULT_CLIMATE_PROFILE.climateRefreshHours);
  if (!customized && refreshHours === 6) refreshHours = DEFAULT_CLIMATE_PROFILE.climateRefreshHours;
  return { refreshHours, refreshHoursCustomized: customized };
}

function normalizeFarmClimateProfile(input = {}) {
  const base = input && typeof input === "object" ? input : {};
  const preferredSource = ["wunderground", "inmet", "manual"].includes(String(base.preferredSource || "")) ? String(base.preferredSource) : DEFAULT_CLIMATE_PROFILE.preferredSource;
  const integrationStatus = ["pending", "online", "delayed", "offline"].includes(String(base.integrationStatus || "")) ? String(base.integrationStatus) : DEFAULT_CLIMATE_PROFILE.integrationStatus;
  const backendMode = ["pending", "proxy", "manual"].includes(String(base.wundergroundBackendMode || "")) ? String(base.wundergroundBackendMode) : DEFAULT_CLIMATE_PROFILE.wundergroundBackendMode;
  const refreshConfig = normalizeFarmClimateRefreshHours(base);
  const boolField = (value, fallback) => typeof value === "boolean" ? value : (value == null ? fallback : !["0","false","off","não","nao"].includes(String(value).trim().toLowerCase()));
  const heatWarning = clamp(base.alertsHeatWarningTempC, 20, 60, DEFAULT_CLIMATE_PROFILE.alertsHeatWarningTempC);
  const heatCritical = Math.max(heatWarning + 1, clamp(base.alertsHeatCriticalTempC, 21, 70, DEFAULT_CLIMATE_PROFILE.alertsHeatCriticalTempC));
  const humidityCritical = clamp(base.alertsLowHumidityCriticalPct, 5, 95, DEFAULT_CLIMATE_PROFILE.alertsLowHumidityCriticalPct);
  const humidityWarning = Math.max(humidityCritical + 1, clamp(base.alertsLowHumidityWarningPct, 6, 100, DEFAULT_CLIMATE_PROFILE.alertsLowHumidityWarningPct));
  const rainWarning = clamp(base.alertsHeavyRainWarningMm, 1, 400, DEFAULT_CLIMATE_PROFILE.alertsHeavyRainWarningMm);
  const rainCritical = Math.max(rainWarning + 1, clamp(base.alertsHeavyRainCriticalMm, 2, 600, DEFAULT_CLIMATE_PROFILE.alertsHeavyRainCriticalMm));
  const dryWarning = clamp(base.alertsDryStreakWarningDays, 1, 60, DEFAULT_CLIMATE_PROFILE.alertsDryStreakWarningDays);
  const dryCritical = Math.max(dryWarning + 1, clamp(base.alertsDryStreakCriticalDays, 2, 90, DEFAULT_CLIMATE_PROFILE.alertsDryStreakCriticalDays));
  return {
    ...DEFAULT_CLIMATE_PROFILE,
    ...base,
    preferredSource,
    integrationStatus,
    wundergroundBackendMode: backendMode,
    weatherUndergroundStationId: String(base.weatherUndergroundStationId || "").trim(),
    weatherUndergroundStationName: String(base.weatherUndergroundStationName || "").trim(),
    fallbackToInmet: typeof base.fallbackToInmet === "boolean" ? base.fallbackToInmet : DEFAULT_CLIMATE_PROFILE.fallbackToInmet,
    inmetStationCode: String(base.inmetStationCode || "").trim(),
    inmetStationName: String(base.inmetStationName || "").trim(),
    maxStationDistanceKm: clamp(base.maxStationDistanceKm, 1, 500, DEFAULT_CLIMATE_PROFILE.maxStationDistanceKm),
    analysisWindowDays: clamp(base.analysisWindowDays, 1, 180, DEFAULT_CLIMATE_PROFILE.analysisWindowDays),
    alertWindowDays: clamp(base.alertWindowDays, 1, 180, DEFAULT_CLIMATE_PROFILE.alertWindowDays),
    historyWindowDays: clamp(base.historyWindowDays, 1, 365, DEFAULT_CLIMATE_PROFILE.historyWindowDays),
    climateRefreshHours: refreshConfig.refreshHours,
    refreshHoursCustomized: refreshConfig.refreshHoursCustomized,
    rainfallWeight: clamp(base.rainfallWeight, 0, 1, DEFAULT_CLIMATE_PROFILE.rainfallWeight),
    dryDaysWeight: clamp(base.dryDaysWeight, 0, 1, DEFAULT_CLIMATE_PROFILE.dryDaysWeight),
    climateRiskThreshold: clamp(base.climateRiskThreshold, 0.5, 1.5, DEFAULT_CLIMATE_PROFILE.climateRiskThreshold),
    alertsHeatEnabled: boolField(base.alertsHeatEnabled, DEFAULT_CLIMATE_PROFILE.alertsHeatEnabled),
    alertsHeatWarningTempC: heatWarning,
    alertsHeatCriticalTempC: heatCritical,
    alertsLowHumidityEnabled: boolField(base.alertsLowHumidityEnabled, DEFAULT_CLIMATE_PROFILE.alertsLowHumidityEnabled),
    alertsLowHumidityWarningPct: humidityWarning,
    alertsLowHumidityCriticalPct: humidityCritical,
    alertsHeavyRainEnabled: boolField(base.alertsHeavyRainEnabled, DEFAULT_CLIMATE_PROFILE.alertsHeavyRainEnabled),
    alertsHeavyRainWarningMm: rainWarning,
    alertsHeavyRainCriticalMm: rainCritical,
    alertsDryStreakEnabled: boolField(base.alertsDryStreakEnabled, DEFAULT_CLIMATE_PROFILE.alertsDryStreakEnabled),
    alertsDryStreakWarningDays: dryWarning,
    alertsDryStreakCriticalDays: dryCritical,
    notes: String(base.notes || ""),
    wundergroundApiKeyGlobal: String(base.wundergroundApiKeyGlobal || base.wunderground_api_key_global || "").trim(),
    climateRetentionYears: clamp(base.climateRetentionYears, 2, 5, 3),
    lastClimateSyncAt: String(base.lastClimateSyncAt || "").trim(),
    lastClimateSource: String(base.lastClimateSource || "").trim(),
    lastClimateSummary: String(base.lastClimateSummary || DEFAULT_CLIMATE_PROFILE.lastClimateSummary).trim(),
    rainfallMm15d: toMetricString(base.rainfallMm15d),
    rainfallMm30d: toMetricString(base.rainfallMm30d),
    rainfallMm90d: toMetricString(base.rainfallMm90d),
    dryDays15d: toMetricString(base.dryDays15d),
    dryDays30d: toMetricString(base.dryDays30d),
    dryDays90d: toMetricString(base.dryDays90d),
    avgTemp15d: toMetricString(base.avgTemp15d),
    avgTemp30d: toMetricString(base.avgTemp30d),
    avgTemp90d: toMetricString(base.avgTemp90d),
    stations: normalizeClimateStations(base.stations, { ...DEFAULT_CLIMATE_PROFILE, ...base, preferredSource, integrationStatus }),
  };
}
function normalizeFarmClimateProfiles(input = {}) {
  const raw = input && typeof input === "object" ? input : {};
  const out = {};
  Object.keys(raw).forEach((farmId) => { out[farmId] = normalizeFarmClimateProfile(raw[farmId]); });
  return out;
}
function getFarmClimateProfile(config, farmId) {
  const profiles = normalizeFarmClimateProfiles(config);
  const globalProfile = normalizeFarmClimateProfile(profiles["__global__"] || null);
  const farmKey = String(farmId || "").trim();
  const farmProfile = normalizeFarmClimateProfile(profiles[farmKey] || null);
  if (!farmKey || farmKey === "__global__") return globalProfile;
  return normalizeFarmClimateProfile({
    ...globalProfile,
    ...farmProfile,
    stations: farmProfile?.stations || [],
  });
}

function normalizeClimateDatabaseSummary(input = null) {
  const raw = input && typeof input === "object" ? input : {};
  const windowsRaw = raw.windows && typeof raw.windows === "object" ? raw.windows : {};
  const cronRaw = raw.cron && typeof raw.cron === "object" ? raw.cron : {};
  const normWindow = (value = {}) => ({
    rainfall_mm: toMetricNumber(value.rainfall_mm, 0),
    dry_days: Math.max(0, Math.trunc(toMetricNumber(value.dry_days, 0))),
    avg_temp_c: value.avg_temp_c == null || value.avg_temp_c === "" ? null : toMetricNumber(value.avg_temp_c, 0),
    temp_min_c: value.temp_min_c == null || value.temp_min_c === "" ? null : toMetricNumber(value.temp_min_c, 0),
    temp_max_c: value.temp_max_c == null || value.temp_max_c === "" ? null : toMetricNumber(value.temp_max_c, 0),
    humidity_min_pct: value.humidity_min_pct == null || value.humidity_min_pct === "" ? null : toMetricNumber(value.humidity_min_pct, 0),
    humidity_max_pct: value.humidity_max_pct == null || value.humidity_max_pct === "" ? null : toMetricNumber(value.humidity_max_pct, 0),
    records_count: Math.max(0, Math.trunc(toMetricNumber(value.records_count, 0))),
  });
  return {
    farmId: String(raw.farm_id || raw.farmId || "").trim(),
    preferredSource: String(raw.preferred_source || raw.preferredSource || "").trim(),
    effectiveSource: String(raw.effective_source || raw.effectiveSource || "manual").trim() || "manual",
    sourceStationId: String(raw.source_station_id || raw.sourceStationId || "").trim(),
    sourceStationName: String(raw.source_station_name || raw.sourceStationName || "").trim(),
    latestObservedOn: String(raw.latest_observed_on || raw.latestObservedOn || "").trim(),
    latestSyncAt: normalizeUtcDateTimeString(raw.latest_sync_at || raw.latestSyncAt || ""),
    integrationStatus: String(raw.integration_status || raw.integrationStatus || "pending").trim() || "pending",
    selectionReason: String(raw.selection_reason || raw.selectionReason || "").trim(),
    selectionMessage: String(raw.selection_message || raw.selectionMessage || "").trim(),
    fallbackUsed: !!(raw.fallback_used || raw.fallbackUsed),
    fallbackChain: Array.isArray(raw.fallback_chain || raw.fallbackChain) ? (raw.fallback_chain || raw.fallbackChain).map((item) => ({
      stationUid: String(item.station_uid || item.stationUid || "").trim(),
      sourceType: String(item.source_type || item.sourceType || "").trim(),
      stationId: String(item.station_id || item.stationId || "").trim(),
      stationName: String(item.station_name || item.stationName || "").trim(),
      distanceKm: item.distance_km == null ? null : toMetricNumber(item.distance_km, 0),
      utcOffsetHours: clamp(item.utc_offset_hours ?? item.utcOffsetHours, -12, 14, -3),
      status: String(item.status || "").trim(),
      reason: String(item.reason || "").trim(),
    })) : [],
    calculation: raw.calculation && typeof raw.calculation === "object" ? {
      stationUsed: raw.calculation.station_used && typeof raw.calculation.station_used === "object" ? {
        sourceType: String(raw.calculation.station_used.source_type || raw.calculation.station_used.sourceType || "").trim(),
        stationId: String(raw.calculation.station_used.station_id || raw.calculation.station_used.stationId || "").trim(),
        stationName: String(raw.calculation.station_used.station_name || raw.calculation.station_used.stationName || "").trim(),
        distanceKm: raw.calculation.station_used.distance_km == null ? null : toMetricNumber(raw.calculation.station_used.distance_km, 0),
      } : null,
      fallbackUsed: !!(raw.calculation.fallback_used || raw.calculation.fallbackUsed),
      fallbackReason: String(raw.calculation.fallback_reason || raw.calculation.fallbackReason || "").trim(),
      fallbackMessage: String(raw.calculation.fallback_message || raw.calculation.fallbackMessage || "").trim(),
      historyEntry: raw.calculation.history_entry && typeof raw.calculation.history_entry === "object" ? raw.calculation.history_entry : null,
    } : null,
    stationStatuses: Array.isArray(raw.station_statuses || raw.stationStatuses) ? (raw.station_statuses || raw.stationStatuses).map((item) => ({
      stationUid: String(item.station_uid || item.stationUid || item.station_id || item.stationId || "").trim(),
      sourceType: String(item.source_type || item.sourceType || "manual").trim() || "manual",
      stationId: String(item.station_id || item.stationId || "").trim(),
      stationName: String(item.station_name || item.stationName || "").trim(),
      latestObservedOn: String(item.latest_observed_on || item.latestObservedOn || "").trim(),
      status: String(item.status || "pending").trim() || "pending",
      isOnline: typeof item.is_online === "boolean" ? item.is_online : !!item.isOnline,
      latestTemp: item.latest_temp == null && item.latestTemp == null ? null : toMetricNumber(item.latest_temp ?? item.latestTemp, 0),
      latestHumidity: item.latest_humidity == null && item.latestHumidity == null ? null : toMetricNumber(item.latest_humidity ?? item.latestHumidity, 0),
      distanceKm: item.distance_km == null ? null : toMetricNumber(item.distance_km, 0),
      utcOffsetHours: clamp(item.utc_offset_hours ?? item.utcOffsetHours, -12, 14, -3),
    })) : [],
    latestSnapshot: raw.latest_snapshot && typeof raw.latest_snapshot === "object" ? {
      observedOn: String(raw.latest_snapshot.observed_on || raw.latest_snapshot.observedOn || "").trim(),
      tempCurrentC: raw.latest_snapshot.temp_current_c == null ? null : toMetricNumber(raw.latest_snapshot.temp_current_c, 0),
      tempMaxC: raw.latest_snapshot.temp_max_c == null ? null : toMetricNumber(raw.latest_snapshot.temp_max_c, 0),
      humidityCurrentPct: raw.latest_snapshot.humidity_current_pct == null ? null : toMetricNumber(raw.latest_snapshot.humidity_current_pct, 0),
      humidityPeakPct: raw.latest_snapshot.humidity_max_pct == null ? null : toMetricNumber(raw.latest_snapshot.humidity_max_pct, 0),
      pressureHpa: raw.latest_snapshot.pressure_hpa == null ? null : toMetricNumber(raw.latest_snapshot.pressure_hpa, 0),
      rainfallMm: raw.latest_snapshot.rainfall_mm == null ? null : toMetricNumber(raw.latest_snapshot.rainfall_mm, 0),
      lastRecordedAt: normalizeUtcDateTimeString(raw.latest_snapshot.last_recorded_at || raw.latest_snapshot.lastRecordedAt || ""),
      sourceType: String(raw.latest_snapshot.source_type || raw.latest_snapshot.sourceType || "").trim(),
      sourceStationId: String(raw.latest_snapshot.source_station_id || raw.latest_snapshot.sourceStationId || "").trim(),
      sourceStationName: String(raw.latest_snapshot.source_station_name || raw.latest_snapshot.sourceStationName || "").trim(),
      utcOffsetHours: clamp(raw.latest_snapshot.utc_offset_hours ?? raw.latest_snapshot.utcOffsetHours, -12, 14, -3),
    } : null,
    summary: String(raw.summary || raw.lastClimateSummary || "Sem dados climáticos no banco").trim() || "Sem dados climáticos no banco",
    dbStats: raw.db_stats && typeof raw.db_stats === "object" ? {
      retentionYears: Math.max(2, Math.min(5, Math.trunc(toMetricNumber(raw.db_stats.retention_years || raw.db_stats.retentionYears, 3)) || 3)),
      readingsCount: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.readings_count || raw.db_stats.readingsCount || raw.db_stats.reading_rows, 0))),
      calcHistoryCount: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.calc_history_count || raw.db_stats.calcHistoryCount || (raw.db_stats.last_prune && raw.db_stats.last_prune.deleted_calculation_history != null ? raw.db_stats.last_prune.deleted_calculation_history : raw.db_stats.daily_rows), 0))),
      totalRows: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.total_rows || raw.db_stats.totalRows || raw.db_stats.daily_rows, 0))),
      sizeBytes: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.size_bytes || raw.db_stats.sizeBytes || raw.db_stats.payload_bytes, 0))),
      sizeMb: toMetricNumber(raw.db_stats.size_mb || raw.db_stats.sizeMb || ((raw.db_stats.size_bytes || raw.db_stats.payload_bytes || 0) / 1048576), 0),
      oldestObservedOn: String(raw.db_stats.oldest_observed_on || raw.db_stats.oldestObservedOn || raw.db_stats.oldest_recorded_at || "").trim(),
      newestObservedOn: String(raw.db_stats.newest_observed_on || raw.db_stats.newestObservedOn || raw.db_stats.newest_recorded_at || "").trim(),
      readingsSizeBytes: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.readings_size_bytes || raw.db_stats.readingsSizeBytes, 0))),
      calcHistorySizeBytes: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.calc_history_size_bytes || raw.db_stats.calcHistorySizeBytes, 0))),
      dailySizeBytes: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.daily_size_bytes || raw.db_stats.dailySizeBytes, 0))),
      stateSizeBytes: Math.max(0, Math.trunc(toMetricNumber(raw.db_stats.state_size_bytes || raw.db_stats.stateSizeBytes, 0))),
      lastPrunedAt: String(raw.db_stats.last_pruned_at || raw.db_stats.lastPrunedAt || (raw.db_stats.last_prune && raw.db_stats.last_prune.cutoff) || "").trim(),
      pruneSummary: String(raw.db_stats.prune_summary || raw.db_stats.pruneSummary || "").trim(),
      cleanupMessage: String(raw.db_stats.cleanup_message || raw.db_stats.cleanupMessage || raw.db_stats.prune_summary || raw.db_stats.pruneSummary || "").trim(),
    } : { retentionYears:3, readingsCount:0, calcHistoryCount:0, totalRows:0, sizeBytes:0, sizeMb:0, oldestObservedOn:"", newestObservedOn:"", lastPrunedAt:"", pruneSummary:"", cleanupMessage:"" },
    autoSyncWindow: raw.auto_sync_window && typeof raw.auto_sync_window === "object" ? {
      graceMinutes: Math.max(0, Math.trunc(toMetricNumber(raw.auto_sync_window.grace_minutes || raw.auto_sync_window.graceMinutes, 5)) || 5),
      shouldDeferAutoSync: !!(raw.auto_sync_window.should_defer_auto_sync || raw.auto_sync_window.shouldDeferAutoSync),
      reason: String(raw.auto_sync_window.reason || "").trim(),
      readyAt: String(raw.auto_sync_window.ready_at || raw.auto_sync_window.readyAt || "").trim(),
      checkedAt: String(raw.auto_sync_window.checked_at || raw.auto_sync_window.checkedAt || "").trim(),
    } : { graceMinutes: 5, shouldDeferAutoSync: false, reason: "", readyAt: "", checkedAt: "" },
    diagnostics: raw.diagnostics && typeof raw.diagnostics === "object" ? {
      severity: String(raw.diagnostics.severity || "ok").trim() || "ok",
      headline: String(raw.diagnostics.headline || "").trim(),
      summary: String(raw.diagnostics.summary || "").trim(),
      latestObservationAgeDays: raw.diagnostics.latest_observation_age_days == null ? null : Math.trunc(toMetricNumber(raw.diagnostics.latest_observation_age_days, 0)),
      stationCounts: raw.diagnostics.station_counts && typeof raw.diagnostics.station_counts === "object" ? {
        total: Math.max(0, Math.trunc(toMetricNumber(raw.diagnostics.station_counts.total, 0))),
        online: Math.max(0, Math.trunc(toMetricNumber(raw.diagnostics.station_counts.online, 0))),
        delayed: Math.max(0, Math.trunc(toMetricNumber(raw.diagnostics.station_counts.delayed, 0))),
        offline: Math.max(0, Math.trunc(toMetricNumber(raw.diagnostics.station_counts.offline, 0))),
        pending: Math.max(0, Math.trunc(toMetricNumber(raw.diagnostics.station_counts.pending, 0))),
      } : { total:0, online:0, delayed:0, offline:0, pending:0 },
      cronStatus: raw.diagnostics.cron_status && typeof raw.diagnostics.cron_status === "object" ? {
        hasHistory: !!raw.diagnostics.cron_status.has_history,
        overallStatus: String(raw.diagnostics.cron_status.overall_status || "pending").trim() || "pending",
        lastRunAt: String(raw.diagnostics.cron_status.last_run_at || "").trim(),
        message: String(raw.diagnostics.cron_status.message || "").trim(),
      } : { hasHistory:false, overallStatus:"pending", lastRunAt:"", message:"" },
      checks: Array.isArray(raw.diagnostics.checks) ? raw.diagnostics.checks.map((item) => ({
        id: String(item.id || "").trim(),
        label: String(item.label || "").trim(),
        status: String(item.status || "pending").trim() || "pending",
        detail: String(item.detail || "").trim(),
      })) : [],
      issues: Array.isArray(raw.diagnostics.issues) ? raw.diagnostics.issues.map((item) => ({
        level: String(item.level || "info").trim() || "info",
        title: String(item.title || "").trim(),
        message: String(item.message || "").trim(),
      })) : [],
    } : { severity:"ok", headline:"", summary:"", latestObservationAgeDays:null, stationCounts:{ total:0, online:0, delayed:0, offline:0, pending:0 }, cronStatus:{ hasHistory:false, overallStatus:"pending", lastRunAt:"", message:"" }, checks:[], issues:[] },
    cronLatestRun: cronRaw.latest_run && typeof cronRaw.latest_run === "object" ? {
      runUid: String(cronRaw.latest_run.run_uid || cronRaw.latest_run.runUid || "").trim(),
      triggerMode: String(cronRaw.latest_run.trigger_mode || cronRaw.latest_run.triggerMode || "").trim(),
      requestedBy: String(cronRaw.latest_run.requested_by || cronRaw.latest_run.requestedBy || "").trim(),
      startedAt: String(cronRaw.latest_run.started_at || cronRaw.latest_run.startedAt || "").trim(),
      finishedAt: String(cronRaw.latest_run.finished_at || cronRaw.latest_run.finishedAt || "").trim(),
      overallStatus: String(cronRaw.latest_run.overall_status || cronRaw.latest_run.overallStatus || "pending").trim() || "pending",
      message: String(cronRaw.latest_run.message || "").trim(),
      farmsProcessed: Math.max(0, Math.trunc(toMetricNumber(cronRaw.latest_run.farms_processed, 0))),
      stationsProcessed: Math.max(0, Math.trunc(toMetricNumber(cronRaw.latest_run.stations_processed, 0))),
      stationsSynced: Math.max(0, Math.trunc(toMetricNumber(cronRaw.latest_run.stations_synced, 0))),
      stationsSkipped: Math.max(0, Math.trunc(toMetricNumber(cronRaw.latest_run.stations_skipped, 0))),
      stationsFailed: Math.max(0, Math.trunc(toMetricNumber(cronRaw.latest_run.stations_failed, 0))),
      metadata: cronRaw.latest_run.metadata && typeof cronRaw.latest_run.metadata === "object" ? cronRaw.latest_run.metadata : null,
    } : null,
    cronRecentRuns: Array.isArray(cronRaw.recent_runs || cronRaw.recentRuns) ? (cronRaw.recent_runs || cronRaw.recentRuns).map((row) => ({
      runUid: String(row.run_uid || row.runUid || "").trim(),
      triggerMode: String(row.trigger_mode || row.triggerMode || "").trim(),
      startedAt: String(row.started_at || row.startedAt || "").trim(),
      finishedAt: String(row.finished_at || row.finishedAt || "").trim(),
      overallStatus: String(row.overall_status || row.overallStatus || "pending").trim() || "pending",
      message: String(row.message || "").trim(),
      stationsProcessed: Math.max(0, Math.trunc(toMetricNumber(row.stations_processed, 0))),
      stationsSynced: Math.max(0, Math.trunc(toMetricNumber(row.stations_synced, 0))),
      stationsSkipped: Math.max(0, Math.trunc(toMetricNumber(row.stations_skipped, 0))),
      stationsFailed: Math.max(0, Math.trunc(toMetricNumber(row.stations_failed, 0))),
      metadata: row.metadata && typeof row.metadata === "object" ? row.metadata : null,
    })) : [],
    windows: {
      15: normWindow(windowsRaw[15] || windowsRaw["15"]),
      30: normWindow(windowsRaw[30] || windowsRaw["30"]),
      90: normWindow(windowsRaw[90] || windowsRaw["90"]),
    },
    calculationHistory: Array.isArray(raw.calculation_history || raw.calculationHistory) ? (raw.calculation_history || raw.calculationHistory).map((row) => ({
      calcUid: String(row.calc_uid || row.calcUid || "").trim(),
      contextType: String(row.context_type || row.contextType || "farm").trim() || "farm",
      contextId: String(row.context_id || row.contextId || "").trim(),
      contextName: String(row.context_name || row.contextName || "").trim(),
      preferredSource: String(row.preferred_source || row.preferredSource || "").trim(),
      effectiveSource: String(row.effective_source || row.effectiveSource || "").trim(),
      sourceStationId: String(row.source_station_id || row.sourceStationId || "").trim(),
      sourceStationName: String(row.source_station_name || row.sourceStationName || "").trim(),
      latestObservedOn: String(row.latest_observed_on || row.latestObservedOn || "").trim(),
      windowDays: Math.max(1, Math.trunc(toMetricNumber(row.window_days || row.windowDays, 30))),
      distanceKm: row.distance_km == null ? null : toMetricNumber(row.distance_km, 0),
      fallbackUsed: !!(row.fallback_used || row.fallbackUsed),
      fallbackReason: String(row.fallback_reason || row.fallbackReason || "").trim(),
      fallbackMessage: String(row.fallback_message || row.fallbackMessage || "").trim(),
      climateData: row.climate_data || row.climateData || null,
      fallbackChain: Array.isArray(row.fallback_chain || row.fallbackChain) ? (row.fallback_chain || row.fallbackChain) : [],
      requestedBy: String(row.requested_by || row.requestedBy || "").trim(),
      createdAt: String(row.created_at || row.createdAt || "").trim(),
    })) : [],
  };
}

function describeFarmClimateProfile(profile, climateSummary = null) {
  const p = normalizeFarmClimateProfile(profile);
  const db = normalizeClimateDatabaseSummary(climateSummary);
  const nearestStation = getNearestClimateStation(p);
  const sourceLabel = db.effectiveSource === "wunderground" ? "WU banco" : (db.effectiveSource === "inmet" ? "INMET banco" : (db.latestObservedOn ? "Manual banco" : (p.preferredSource === "wunderground" ? "WU principal" : (p.preferredSource === "inmet" ? "INMET principal" : "Manual/offline"))));
  const fallbackLabel = p.fallbackToInmet ? "INMET fallback" : "sem fallback";
  const statusLabel = db.integrationStatus === "online" ? "Online" : (db.integrationStatus === "delayed" ? "Em atraso" : (db.latestObservedOn ? "Com dados" : "Sem dados"));
  const stationLabel = db.sourceStationId || db.sourceStationName || nearestStation?.stationName || nearestStation?.stationId || (p.preferredSource === "wunderground" ? (p.weatherUndergroundStationId || "sem estação WU") : (p.inmetStationCode || "sem estação INMET"));
  const summary = db.summary || p.lastClimateSummary || `${sourceLabel} · ${fallbackLabel}`;
  return { sourceLabel, fallbackLabel, statusLabel, stationLabel, summary, climateSummary: summary, latestObservedOn: db.latestObservedOn, latestSyncAt: db.latestSyncAt, nearestStation };
}


function pickWindowMetric(profile, summary, keyPrefix, windowDays) {
  const p = normalizeFarmClimateProfile(profile);
  const db = normalizeClimateDatabaseSummary(summary);
  const normalizedWindow = Number(windowDays) === 15 ? 15 : (Number(windowDays) === 90 ? 90 : 30);
  if (db.latestObservedOn) {
    const win = db.windows[normalizedWindow] || {};
    if (keyPrefix === 'rainfallMm') return toMetricNumber(win.rainfall_mm, 0);
    if (keyPrefix === 'dryDays') return toMetricNumber(win.dry_days, 0);
    if (keyPrefix === 'avgTemp') return win.avg_temp_c == null ? 0 : toMetricNumber(win.avg_temp_c, 0);
  }
  return toMetricNumber(p[`${keyPrefix}${normalizedWindow}d`], 0);
}

function computeClimateCapacityAdjustment(profile, climateSummary = null) {
  const p = normalizeFarmClimateProfile(profile);
  const db = normalizeClimateDatabaseSummary(climateSummary);
  const windowDays = Number(p.analysisWindowDays) === 15 ? 15 : (Number(p.analysisWindowDays) === 90 ? 90 : 30);
  const rainfallMm = pickWindowMetric(p, db, 'rainfallMm', windowDays);
  const dryDays = pickWindowMetric(p, db, 'dryDays', windowDays);
  const avgTemp = pickWindowMetric(p, db, 'avgTemp', windowDays);
  if (!db.latestObservedOn) {
    return {
      factor: 1,
      factorLabel: '1.00x',
      rainfallMm: 0,
      dryDays: 0,
      avgTemp: null,
      windowDays,
      summary: 'Sem dados climáticos gravados no banco de dados',
      source: p.preferredSource,
      integrationStatus: 'pending',
      condition: 'sem dados',
      backendSummary: db,
    };
  }
  const normalizedRain = windowDays <= 15 ? clamp(rainfallMm / 80, 0, 1.35, 0) : (windowDays >= 90 ? clamp(rainfallMm / 240, 0, 1.35, 0) : clamp(rainfallMm / 140, 0, 1.35, 0));
  const maxDry = Math.max(5, Math.round(windowDays * 0.8));
  const normalizedDry = clamp(dryDays / maxDry, 0, 1, 0);
  const tempPenalty = avgTemp > 0 ? clamp(Math.abs(avgTemp - 26) / 18, 0, 0.18, 0) : 0;
  const weightedRain = normalizedRain * clamp(p.rainfallWeight, 0, 1, 0.7);
  const weightedDry = normalizedDry * clamp(p.dryDaysWeight, 0, 1, 0.3);
  const rawFactor = 0.78 + weightedRain - (weightedDry * 0.28) - tempPenalty;
  const factor = clamp(rawFactor, 0.6, 1.15, 1);
  let condition = 'estável';
  if (factor <= 0.8) condition = 'seca forte / capacidade reduzida';
  else if (factor <= 0.92) condition = 'atenção climática / capacidade reduzida';
  else if (factor >= 1.05) condition = 'janela favorável / capacidade levemente ampliada';
  const sourceLabel = db.effectiveSource === 'wunderground' ? 'WU banco' : (db.effectiveSource === 'inmet' ? 'INMET banco' : 'manual banco');
  const summary = `${sourceLabel} · ${windowDays}d · ${rainfallMm} mm · ${dryDays} dias secos · fator ${factor.toFixed(2)} (${condition})`;
  return { factor, factorLabel: `${factor.toFixed(2)}x`, rainfallMm, dryDays, avgTemp, windowDays, summary, source: db.effectiveSource || p.preferredSource, integrationStatus: db.integrationStatus || p.integrationStatus, condition, backendSummary: db };
}

function describeClimateComputation(profile, climateSummary = null) {
  const calc = computeClimateCapacityAdjustment(profile, climateSummary);
  return { factorLabel: calc.factorLabel, summary: calc.summary, condition: calc.condition, rainfallMm: calc.rainfallMm, dryDays: calc.dryDays, avgTemp: calc.avgTemp, windowDays: calc.windowDays };
}


export {
  DEFAULT_CLIMATE_STATION,
  DEFAULT_CLIMATE_PROFILE,
  normalizeFarmClimateProfile,
  normalizeFarmClimateProfiles,
  normalizeClimateDatabaseSummary,
  getFarmClimateProfile,
  describeFarmClimateProfile,
  computeClimateCapacityAdjustment,
  describeClimateComputation,
  normalizeClimateStation,
  normalizeClimateStations,
  getNearestClimateStation,
};
